'use client';

import classNames from 'classnames';
import { Suspense, useMemo } from 'react';
import useAlgoliaConfigs from '@alltrails/context/hooks/useAlgoliaConfigs';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { useExperiments } from '@alltrails/experiments';
import getNaturalLanguageSearchUrl from '@alltrails/search/utils/getNaturalLanguageSearchUrl';
import AdaChatbot from '@alltrails/shared/components/AdaChatbot';
import useAdaChatbot from '../../hooks/useAdaChatbot';
import ConditionalRenderer from './ConditionalRender';
import Dropdowns from './Dropdowns';
import LogoLink from './LogoLink';
import MobileMenu from './MobileMenu';
import SearchBox, { SearchBoxProps } from './SearchBox';
import SkipToMainContentButton from './SkipToMainContentButton';
import styles from './styles/styles.module.scss';
import { HeaderBackground } from './types';
import UserCTA from './UserCTA';

// https://www.figma.com/design/uArl1Yn1JPudAlXAqYFGs5/Global-Nav-[Handoff]?node-id=63-5827&t=2hSsVfor4WO27vQn-0

type NavigationHeaderProps = {
  background?: HeaderBackground;
  bottomBorder?: boolean;
  className?: string;
  edgeToEdge?: boolean;
  hideSearch?: boolean;
  onInputBlur?: SearchBoxProps['onInputBlur'];
  onInputFocus?: SearchBoxProps['onInputFocus'];
  searchClearOnSelect?: SearchBoxProps['clearOnSelect'];
  searchGetCurrentLatLng?: SearchBoxProps['getCurrentLatLng'];
  searchOnAddressSelect?: SearchBoxProps['onAddressSelect'];
  searchOnNaturalLanguageResultSelect?: SearchBoxProps['onNaturalLanguageResultSelect'];
  searchOnResultSelect?: SearchBoxProps['onResultSelect'];
  searchRequestLoading?: SearchBoxProps['searchRequestLoading'];
  searchPreventNavigationOnSelect?: SearchBoxProps['navigateOnSelect'];
  searchValue?: SearchBoxProps['value'];
  tertiarySearchElement?: SearchBoxProps['tertiaryElement'];
};

const NavigationHeader = ({
  background = 'primary',
  bottomBorder,
  className,
  edgeToEdge,
  hideSearch,
  onInputBlur,
  onInputFocus,
  searchClearOnSelect,
  searchGetCurrentLatLng,
  searchOnAddressSelect,
  searchOnNaturalLanguageResultSelect,
  searchOnResultSelect,
  searchPreventNavigationOnSelect = false,
  searchValue,
  tertiarySearchElement,
  searchRequestLoading
}: NavigationHeaderProps) => {
  const algoliaConfigs = useAlgoliaConfigs();
  const languageRegionCode = useLanguageRegionCode();
  const experiments = useExperiments();
  const { isAdaReady, config } = useAdaChatbot();

  const naturalLanguageSearchEnabled = experiments['web-ai-natural-language-search']?.value === 'treatment';
  const handleNaturalLanguageResultSelect = useMemo<SearchBoxProps['onNaturalLanguageResultSelect']>(() => {
    if (!naturalLanguageSearchEnabled) {
      return undefined;
    }
    return searchOnNaturalLanguageResultSelect
      ? searchOnNaturalLanguageResultSelect
      : (query, clickPosition, selectionType, searchOrigin) => {
          const url = getNaturalLanguageSearchUrl({ query, clickPosition, languageRegionCode, selectionType, searchOrigin });
          window.location.assign(url);
        };
  }, [languageRegionCode, naturalLanguageSearchEnabled, searchOnNaturalLanguageResultSelect]);

  return (
    <header
      className={classNames(
        styles.header,
        styles[background],
        {
          [styles.bottomBorder]: bottomBorder,
          [styles.edgeToEdge]: edgeToEdge
        },
        className
      )}
    >
      <SkipToMainContentButton />
      <nav className={styles.nav}>
        <div className={styles.left}>
          <LogoLink background={background} />
        </div>
        {algoliaConfigs && !hideSearch ? (
          <SearchBox
            configs={algoliaConfigs}
            clearOnSelect={searchClearOnSelect}
            getCurrentLatLng={searchGetCurrentLatLng}
            navigateOnSelect={!searchPreventNavigationOnSelect}
            onAddressSelect={searchOnAddressSelect}
            onInputBlur={onInputBlur}
            onInputFocus={onInputFocus}
            onNaturalLanguageResultSelect={handleNaturalLanguageResultSelect}
            onResultSelect={searchOnResultSelect}
            searchRequestLoading={searchRequestLoading}
            tertiaryElement={tertiarySearchElement}
            value={searchValue}
          />
        ) : null}
        <div className={styles.right}>
          <Suspense fallback={null}>
            <ConditionalRenderer renderWhen="mobile">
              <MobileMenu />
            </ConditionalRenderer>
            <ConditionalRenderer renderWhen="desktop">
              <Dropdowns background={background} />
              <UserCTA background={background} />
            </ConditionalRenderer>
          </Suspense>
          {isAdaReady && <AdaChatbot config={config} experiment />}
        </div>
      </nav>
    </header>
  );
};
export default NavigationHeader;
